// src/components/landing/default/hollo-landing-default.ts
import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { UiMessage } from "../../../types/chat";
import "../../audio-player/hollo-audio-player";
import "../../inline-recorder/hollo-inline-recorder";
import "../../profile-image/hollo-profile-image";
import { landingStyles as holloLandingStyles } from "../../../styles/default/landing-styles";
import {
  micIconSvg,
  waveIconSvg,
  videoSvg,
  figmaCopySvg,
  checkIconSvg,
  figmaVolumeMaxSvg,
  pauseIconSvg,
  playIconSvg,
  deleteIconSvg,
  recordingDotSvg,
  sendUpIconSvg,
  stopIconSvg,
} from "../../icons";
import { LandingMixin } from "../landing-mixin";

@customElement("hollo-landing-default")
export class HolloLandingDefault extends LandingMixin(LitElement) {
  @property() fileId = "";
  @property() alt = "";
  @property() fullName = "";
  @property({ type: Array }) messages: UiMessage[] = [];
  @property({ type: Boolean }) typing = false;
  @property({ type: Boolean }) live = false;
  @property() profileImageId = "";
  @property() currentVideoFor?: string;

  private renderMessage(m: UiMessage) {
    return html`
      <div
        class="hollo-row ${m.is_human
          ? "hollo-right"
          : "hollo-left"} ${m.audioUrl && !m.is_human ? "hollo-isAiVoice" : ""}"
      >
        <div class="hollo-bubble-box ${m.audioUrl ? "hollo-audio-bubble" : ""}">
          <div class="hollo-bubble-box-inner">
            <div style="position: relative">
              <div class="hollo-bubble ">
                ${m.audioUrl
                  ? html`
                      <hollo-audio-player
                        .src=${m.audioUrl}
                        .autoplay=${!!m.autoplay}
                        .isAiVoice=${!m.is_human}
                        .forMessageId=${m.for_message_id}
                      ></hollo-audio-player>
                      ${m.content && !m.is_human
                        ? html`<div class="hollo-caption hollo-transcript">
                            ${m.content}
                          </div>`
                        : null}
                    `
                  : html`${m.content || (m._optimistic ? "…" : "")}`}
                ${!m.is_human
                  ? html`<div class="hollo-message-footer">
                      <div class="hollo-message-icons">
                        ${m.content
                          ? html`<button
                              class="hollo-message-icon-btn ${this
                                .copiedMessageId === (m.id || "")
                                ? "hollo-icon-copied"
                                : ""}"
                              @click=${() =>
                                this.copyMessageText(
                                  m.content || "",
                                  m.id || ""
                                )}
                              title="Copy message"
                              aria-label="Copy message"
                            >
                              <span class="hollo-icon-wrapper">
                                ${this.copiedMessageId === (m.id || "")
                                  ? checkIconSvg(18)
                                  : figmaCopySvg(18)}
                              </span>
                            </button>`
                          : null}
                        ${!m.audioUrl
                          ? html`<button
                              class="hollo-message-icon-btn ${this.getAudioState(
                                m.id || ""
                              ) === "playing"
                                ? "hollo-audio-playing"
                                : ""}"
                              @click=${() => {
                                if (this.isAudioPlaying(m.id || "")) {
                                  window.dispatchEvent(
                                    new CustomEvent("hollo-toggle-audio", {
                                      detail: { forMessageId: m.id },
                                    })
                                  );
                                } else {
                                  this.requestAudio(m.id || "");
                                }
                              }}
                              title=${this.getAudioState(m.id || "") ===
                              "playing"
                                ? "Pause audio"
                                : "Play message"}
                              aria-label=${this.getAudioState(m.id || "") ===
                              "playing"
                                ? "Pause audio"
                                : "Play message"}
                            >
                              <span class="hollo-icon-wrapper">
                                ${m.loadingVoice
                                  ? html`<span
                                      class="hollo-spinner-small"
                                      aria-hidden="true"
                                    ></span>`
                                  : this.getAudioState(m.id || "") === "playing"
                                  ? pauseIconSvg(22)
                                  : this.getAudioState(m.id || "") === "paused"
                                  ? playIconSvg(22)
                                  : figmaVolumeMaxSvg(22)}
                              </span>
                            </button>`
                          : null}
                        ${this.live
                          ? html`<button
                              class="hollo-message-icon-btn ${this
                                .currentVideoFor === m.id
                                ? "hollo-video-active"
                                : ""}"
                              @click=${() =>
                                this.dispatchEvent(
                                  new CustomEvent("request-video", {
                                    detail: { chatMessageId: m.id },
                                    bubbles: true,
                                    composed: true,
                                  })
                                )}
                              title=${this.currentVideoFor === m.id
                                ? "Stop video"
                                : "Show video for this message"}
                              aria-label=${this.currentVideoFor === m.id
                                ? "Stop video"
                                : "Show video for this message"}
                            >
                              <span class="hollo-icon-wrapper">
                                ${m.loadingVideo
                                  ? html`<span
                                      class="hollo-spinner-small"
                                      aria-hidden="true"
                                    ></span>`
                                  : this.currentVideoFor === m.id
                                  ? stopIconSvg(22)
                                  : videoSvg(18)}
                              </span>
                            </button>`
                          : null}
                      </div>
                      ${this.formatTimestamp(m.created_at)
                        ? html`<div class="hollo-message-timestamp">
                            ${this.formatTimestamp(m.created_at)}
                          </div>`
                        : null}
                    </div>`
                  : m.is_human
                  ? html`<div
                      class="hollo-message-footer hollo-user-message-footer"
                    >
                      ${this.formatTimestamp(m.created_at)
                        ? html`<div class="hollo-message-timestamp">
                            ${this.formatTimestamp(m.created_at)}
                          </div>`
                        : null}
                    </div>`
                  : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div
        class="hollo-landing ${this.live ? "hollo-live-landing-height" : ""}"
      >
        <div class="hollo-messages" aria-live="polite">
          ${this.messages.length === 0 && !this.typing
            ? !this.live
              ? html`
                  <div class="hollo-empty-container">
                    <hollo-profile-image
                      .fileId=${this.fileId}
                      .alt=${this.alt}
                      .size=${100}
                    ></hollo-profile-image>
                    <div class="hollo-prompt">
                      ${this.fullName
                        ? `Ask ${this.fullName} Anything, Or Share Your Feedback.`
                        : "Ask Us Anything, Or Share Your Feedback."}
                    </div>
                  </div>
                `
              : html`<div class="hollo-empty-container"></div>`
            : html`
                ${(() => {
                  const rendered = new Set<string>();
                  const parts: unknown[] = [];
                  for (const m of this.messages) {
                    if (!m || !m.id) continue;
                    if (!m.is_human && m.custom_id && rendered.has(m.id)) {
                      continue;
                    }
                    parts.push(this.renderMessage(m));
                    if (m.is_human && m.custom_id) {
                      for (const r of this.messages) {
                        if (
                          r &&
                          !r.is_human &&
                          r.custom_id &&
                          r.custom_id === m.custom_id
                        ) {
                          rendered.add(r.id!);
                          parts.push(this.renderMessage(r));
                        }
                      }
                    }
                  }
                  return parts;
                })()}
                ${this.typing
                  ? html`<div class="hollo-row hollo-left">
                      <div class="hollo-bubble-box">
                        <div class="hollo-bubble hollo-typing">
                          <span class="hollo-dot"></span
                          ><span class="hollo-dot"></span
                          ><span class="hollo-dot"></span>
                        </div>
                      </div>
                    </div>`
                  : null}
              `}
        </div>

        <div
          class="hollo-composer ${this.showRecorder
            ? "hollo-recorder-active"
            : ""}"
        >
          ${this.showRecorder
            ? html`
                <div class="hollo-recorderBarContainer">
                  <button
                    class="hollo-recorderBinButton"
                    title="Cancel recording"
                    aria-label="Cancel recording"
                    @click=${this.closeRecorder}
                  >
                    <div class="hollo-recorderBinIcon">${deleteIconSvg()}</div>
                  </button>

                  <div class="hollo-recorderBar">
                    <div
                      aria-hidden="true"
                      class="hollo-recorderBarBorder"
                    ></div>
                    <div class="hollo-recorderBarInner">
                      <div class="hollo-recorderBarContent">
                        <div class="hollo-recordingDot hollo-blink">
                          ${recordingDotSvg()}
                        </div>
                        <div class="hollo-timerWaveformContainer">
                          <div class="hollo-timerText">
                            <p>${this.formatSeconds(this.recordingSeconds)}</p>
                          </div>
                          <div
                            class="hollo-waveform ${this.isUserTalking()
                              ? "hollo-talking"
                              : ""}"
                          >
                            ${this.waveformLevels.map(
                              (level) =>
                                html`<span
                                  class="hollo-waveform-bar"
                                  style="height:${Math.max(4, level)}%"
                                ></span>`
                            )}
                          </div>
                        </div>
                        <div class="hollo-recorderSendContainer">
                          <button
                            class="hollo-recorderSendButton"
                            title="Send recording"
                            aria-label="Send recording"
                            @click=${() => this.triggerRecorderSend()}
                          >
                            <div class="hollo-recorderUpIcon">
                              ${sendUpIconSvg()}
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hollo-inline-recorder
                    style="display:none"
                    .open=${this.showRecorder}
                    .bands=${this.waveformBarCount}
                    @send-audio=${(
                      e: CustomEvent<{ base64: string; mimeType: string }>
                    ) => this.onSendAudio(e)}
                    @cancel-recorder=${this.closeRecorder}
                  ></hollo-inline-recorder>
                </div>
              `
            : html`
                <div class="hollo-messageBarContainer">
                  <div class="hollo-messageBar">
                    <div
                      aria-hidden="true"
                      class="hollo-messageBarBorder"
                    ></div>

                    <div class="hollo-messageBarInner">
                      <div class="hollo-messageBarContent">
                        <div class="hollo-messageBarText">
                          <input
                            class="hollo-message-input"
                            type="text"
                            placeholder="Write your message..."
                            .value=${this.message}
                            @input=${this.onInput}
                            @keydown=${(e: KeyboardEvent) =>
                              e.key === "Enter" ? this.send() : null}
                          />
                        </div>
                        <!-- Default style: show send icon when typing, waves + mic when empty -->
                        ${!this.message.trim()
                          ? html`
                              <button
                                class="hollo-waves"
                                title="Start voice call"
                                aria-label="Start voice call"
                                @click=${this.startCallFromWave}
                              >
                                <div class="hollo-wavesGroup">
                                  <div class="hollo-wavesGroupInner">
                                    ${waveIconSvg()}
                                  </div>
                                </div>
                              </button>
                              <button
                                class="hollo-icon-btn hollo-mic"
                                title="Voice input"
                                aria-label="Voice input"
                                @click=${this.openRecorder}
                                ?disabled=${this.showRecorder}
                              >
                                ${micIconSvg(24, "#000000", 1)}
                              </button>
                            `
                          : html`
                              <button
                                class="hollo-send"
                                title="Send"
                                aria-label="Send message"
                                @click=${() => this.send()}
                              >
                                <div class="hollo-up2">${sendUpIconSvg()}</div>
                              </button>
                            `}
                      </div>
                    </div>
                  </div>
                </div>
              `}
        </div>
      </div>
    `;
  }

  protected updated(changed: Map<string, unknown>) {
    super.updated(changed);
    this.handleUpdated(changed);
  }

  connectedCallback() {
    super.connectedCallback();
    this.handleConnected();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.handleDisconnected();
  }

  static get styles() {
    return [holloLandingStyles];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "hollo-landing-default": HolloLandingDefault;
  }
}
